import { ArrowLeftCircleIcon, ClipboardDocumentListIcon, InformationCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useNavigate, useParams } from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import { Loader } from "../../component";
import { AccountRequest, PortfolioContext } from "./context";
import { Fund, FundListContext } from "../fund/context";
import moment from "moment";
import { PersonaContext } from "../login/context";

interface ClipboardState {
    accountBank: boolean
    accountName: boolean
    accountNumber: boolean
}

export default function Request() {
    const [loading, setLoading] = useState(true)
    const [fund, setFund] = useState({} as Fund)
    const [request, setRequest] = useState({} as AccountRequest)
    const [cancelLoading, setCancelLoading] = useState(false)
    const [cancelFailed, setCancelFailed] = useState(false)
    
    const [clipboard, setClipboard] = useState<ClipboardState>({
        accountBank: false,
        accountName: false,
        accountNumber: false,
    })

    const navigate = useNavigate()
    const persona = useContext(PersonaContext)
    const portfolio = useContext(PortfolioContext)
    const fundList = useContext(FundListContext)
    
    const {requestId} = useParams()
    useEffect(() => {
        if (!persona.clientId || !persona.accountId) {
            navigate("/auth/email")
            return
        }
        const request = portfolio.accountRequests?.filter(r => r.id === requestId)[0]
        if (!request) {
            portfolio.reloadAccountRequests()
            return
        }
        
        const fund = fundList.funds?.filter(f => f.id === request.fundId)[0]
        if (!fund) {
            return
        }
        setLoading(false)
        setRequest(request)
        setFund(fund)
    },[ portfolio.accountRequestsLoading, fundList.funds ])

    const copyToClipboard = (field: string, text: string) => {
        setClipboard(prev => ({ ...prev, [field]: true }))
        setTimeout(() => {
            setClipboard(prev => ({ ...prev, [field]: false }))
        }, 1000)
        window.navigator.clipboard.writeText(text);
    }

    const handleCancel = () => {
        if (!requestId) {
            return
        }
        setCancelLoading(true)
        portfolio.cancelRequest(requestId, () => {
            navigate("/portfolio")
        }, () => {
            setCancelLoading(false)
            setCancelFailed(true)
        })
    }

    const renderTitle = () => {
        if (request.switchId && request.type === "redeem") {
            return "switch out request"
        }
        if (request.switchId && request.type === "purchase") {
            return "switch in request"
        }
        if (request.rebateId && request.type === "purchase") {
            return "rebate"
        }
        if (request.type === "purchase") {
            return "investment request"
        }
        if (request.type === "redeem") {
            return "redemption request"
        }
        return request.type
    }
    

    if (loading) {
        return <div className="h-full flex justify-center items-center">
            <Loader classNames="w-16 h-16 text-blue-100" />
        </div>
    }

    return <div className="pb-24">
        <div className="relative h-32 text-center hg-bg-blue mb-10" style={{ borderBottomLeftRadius: "40%", borderBottomRightRadius: "40%"}}>
            <ArrowLeftCircleIcon className="fixed mt-2 ml-2 w-8 h-8 text-white hg-bg-blue rounded-2xl cursor-pointer" onClick={() => navigate("/portfolio")}/>
            <h1 className="pt-16 text-xl text-white pl-3 pr-6 capitalize">{renderTitle()}</h1>
        </div>

        <div className="flex flex-col 2xs:w-5/6 md:w-1/2 mr-auto ml-auto gap-3">
            <div className="flex justify-between border-b pb-3">
                <span className="font-medium text-blue-950 pr-10">Fund</span>
                <span>{fund?.name}</span>
            </div>
            <div className="flex justify-between border-b pb-3">
                <span className="font-medium text-blue-950 pr-10">Class</span>
                <span className="uppercase">{fund?.classes[parseInt(request.fundClassSequence) - 1]?.label}</span>
            </div>
            
            {request.rebateId && request.type === "purchase" && <div className="flex justify-between border-b pb-3">
                <span className="font-medium text-blue-950 pr-10">Rebate period</span>
                <div className="flex flex-col">
                    <span>{moment(request.rebateFromDate).format("DD MMM YYYY")}</span>
                    <span>{moment(request.rebateToDate).format("DD MMM YYYY")}</span>
                </div>
            </div>}

            {request.amount > 0 && <div className="flex justify-between border-b pb-3">
                <span className="font-medium text-blue-950 pr-10">Amount</span>
                <span>{request.amount?.toLocaleString("en-my", { currency: request.fundClassBaseCurrency, style: "currency", maximumFractionDigits: 2, minimumFractionDigits: 2 })}</span>
            </div>}
            {!request.amount && request.requestedAmount && <div className="flex justify-between border-b pb-3">
                <span className="font-medium text-blue-950 pr-10">Amount</span>
                <span>{request.requestedAmount?.toLocaleString("en-my", { currency: request.fundClassBaseCurrency, style: "currency", maximumFractionDigits: 2, minimumFractionDigits: 2 })}</span>
            </div>}
            
            {request.type === "purchase" && <div className="flex justify-between border-b pb-3">
                <span className="font-medium text-blue-950 pr-10">Sales charge %</span>
                <span>{request.salesChargePercentage ? request.salesChargePercentage?.toLocaleString("en-my", { maximumFractionDigits: 2, minimumFractionDigits: 2 }) : "0.00"}%</span>
            </div>}
            {!request.switchId && request.type === "redeem" && <div className="flex justify-between border-b pb-3">
                <span className="font-medium text-blue-950 pr-10">Redemption charge %</span>
                <span>{request.redemptionChargePercentage ? request.redemptionChargePercentage?.toLocaleString("en-my", { maximumFractionDigits: 2, minimumFractionDigits: 2 }) : "0.00"}%</span>
            </div>}
            {request.postFeeAmount > 0 && <div className="flex justify-between border-b pb-3">
                <span className="font-medium text-blue-950 pr-10">Nett amount</span>
                <span>{request.postFeeAmount?.toLocaleString("en-my", { currency: request.fundClassBaseCurrency, style: "currency", maximumFractionDigits: 2, minimumFractionDigits: 2 })}</span>
            </div>}
            
            {request.units > 0 && <div className="flex justify-between border-b pb-3">
                <span className="font-medium text-blue-950 pr-10">Units</span>
                <span>{request.units?.toLocaleString("en-my", { maximumFractionDigits: 4, minimumFractionDigits: 4 })}</span>
            </div>}

            <div className="flex justify-between border-b pb-3">
                <span className="font-medium text-blue-950 pr-10">Status</span>
                <span className="capitalize">{request.status}</span>
            </div>

            <div className="flex justify-between pb-3">
                <span className="font-medium text-blue-950 pr-10">Created on</span>
                <span>{moment(request.createdAt).format("DD MMMM YYYY")}</span>
            </div> 
        </div>

        {!request.switchId && request.type === "purchase" && request.status === "pending" && 
            <>
                <div className="text-sm font-medium bg-amber-200 text-amber-800 border-amber-300 rounded-lg p-5 mb-5 2xs:w-5/6 md:w-1/2 mr-auto ml-auto mt-5">
                    We will match your deposit using the bank account name. Please make sure you deposit from a bank account in your name.
                </div>
                <div className="flex flex-col gap-3 border border-dashed border-amber-700 2xs:w-5/6 md:w-1/2 mr-auto ml-auto p-3 mb-5">
                    <h3 className="text-lg font-semibold text-amber-800 text-center">Bank account</h3>
                    <div className="flex flex-col gap-1 cursor-pointer" onClick={() => copyToClipboard("accountBank", fund.bankAccounts?.collection?.accountBank)}>
                        <span className="font-medium text-amber-800">Bank name</span>
                        <span>{clipboard.accountBank ? "Copied!" : fund.bankAccounts?.collection?.accountBank}</span>
                    </div>
                    <div className="flex flex-col gap-1 cursor-pointer" onClick={() => copyToClipboard("accountName", fund.bankAccounts?.collection?.accountName)}>
                        <span className="font-medium text-amber-800">Account name</span>
                        <span>{clipboard.accountName ? "Copied!" : fund.bankAccounts?.collection?.accountName}</span>
                    </div>
                    <div className="flex flex-col gap-1 cursor-pointer" onClick={() => copyToClipboard("accountNumber", fund.bankAccounts?.collection?.accountNumber)}>
                        <span className="font-medium text-amber-800">Account number</span>
                        <span>{clipboard.accountNumber ? "Copied!" : fund.bankAccounts?.collection?.accountNumber}</span>
                    </div>
                    <div className="flex flex-col gap-1">
                        <span className="font-medium text-amber-800">Reference number</span>
                        <span>Not required</span>
                    </div>
                </div>
            </>}
        {request.status === "pending" && (((request.type === "purchase" && !request.switchId) && persona.canInvest) || (request.type === "redeem" && persona.canRedeem)) && <div className="flex flex-col items-center">
            <button onClick={handleCancel} disabled={cancelLoading} className="bg-white disabled:text-red-200 mt-12 2xs:w-5/6 md:w-1/2 p-3 text-red-500 border-red-500 border shadow-2xl rounded-full outline-0">
                {!cancelLoading ? "Cancel" : <Loader classNames="w-6 h-6 fill-red-600 text-red-100" />}
            </button>
            {cancelFailed && <div className="mt-5 2xs:w-5/6 md:w-1/2 p-3 border border-red-700 bg-red-100 text-sm rounded-full text-center">
                An error ocurred, please try again.
            </div>}
        </div>}


    </div>
}
